import React, { useState, useEffect } from 'react';
import { FaEye, FaEdit, FaTrashAlt, FaPlus, FaUpload, FaTimes, FaImage } from 'react-icons/fa';
import GenericTable from "../../../DynamicComp/Table";
import Button from '../../../DynamicComp/Button';
import Input from '../../../DynamicComp/Input';
import axiosClient, { deleteData } from '../../../../global/Globalapi';
// 1. IMPORT showAlert UTILITY
import { showAlert } from '../../../DynamicComp/Alert';

const ProductGroupImg = ({ onImagesUpdate, initialImages, imageBasePath, deletePath })=> {
    const [editId, setEditId] = useState(null);
    const [workingImages, setWorkingImages] = useState([]);
    const [newImage, setNewImage] = useState({
        file: null,
        displayOrder: '',
        description: '',
        file_name: ''
    });
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const baseURL = axiosClient.defaults.baseURL

    useEffect(() => {
        if (initialImages && initialImages.length > 0) {
            const formattedImages = initialImages.map(img => ({
                id: img.id || img.image_id,
                file: img.file || (img.file_name ? `${baseURL}/uploads/product_type_image
                    /${img.file_name}` : null),
                file_name: img.file_name,
                displayOrder: img.displayOrder || img.display_order || 0,
                description: img.description || '',
                isUpdated: false,
                isNew: false // Ensure existing images are marked as NOT new
            }));
            setWorkingImages(formattedImages);
        } else {
            setWorkingImages([]);
        }
    }, [initialImages]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (onImagesUpdate) {
                onImagesUpdate(workingImages);
            }
        }, 300);
        
        return () => clearTimeout(timer);
    }, [workingImages]);

    useEffect(() => {
        return () => {
            if (selectedImage instanceof File) {
                URL.revokeObjectURL(selectedImage);
            }
        };
    }, [selectedImage]);

    const getImageUrl = (image) => {
        if (image.file instanceof File) {
            return URL.createObjectURL(image.file);
        }
        if (image.file_name) {
            return `${baseURL}/uploads/${imageBasePath}/${image.file_name}`;
        }
        return null;
    };
    
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setNewImage({
            ...newImage,
            file: file,
            file_name: file?.name || ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewImage(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setNewImage({ file: null, displayOrder: '', description: '', file_name: '' });
        setEditId(null);
        setShowUploadForm(false);
    };

    const handleSaveImage = () => {
        if (!newImage.file && !editId) {
            // 2. REPLACE basic alert with showAlert
            showAlert({ 
                type: 'warning', 
                title: 'Missing File', 
                message: 'Please select an image file to upload or update.' 
            });
            return;
        }

        const updatedImage = {
            id: editId || Date.now(),
            file: newImage.file,
            file_name: newImage.file instanceof File ? newImage.file.name : newImage.file_name,
            displayOrder: parseInt(newImage.displayOrder) || 0,
            description: newImage.description || '',
            isUpdated: true,
            isNew: editId === null // Set isNew flag for newly created items
        };

        setWorkingImages(prev => {
            if (editId) {
                // Merge updated image data while preserving original isNew state if it exists
                return prev.map(img => img.id === editId ? { ...img, ...updatedImage, isNew: img.isNew || false } : img);
            }
            return [...prev, updatedImage];
        });

        // 3. OPTIONAL: Add a success alert on save
        showAlert({ 
            type: 'success', 
            title: editId ? 'Image Updated' : 'Image Added', 
            message: `The image has been successfully ${editId ? 'updated' : 'added'} to the list.` 
        });

        resetForm();
    };

    // 4. REWRITE handleDelete to use showAlert for confirmation and feedback
    const handleDelete = (id, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        const imageToDelete = workingImages.find(img => img.id === id);

        showAlert({
            type: 'warning',
            title: 'Confirm Deletion',
            message: `Are you sure you want to delete the image: ${imageToDelete?.file_name || 'New Image'}? This action cannot be undone.`,
            dangerMode: true, // sweetalert prop to make button red
            buttons: ['Cancel', 'Yes, Delete it!'], // sweetalert prop to customize buttons
        }, async () => {
            // This callback runs IF the user confirms ('Yes, Delete it!')
            try {
                // If it's a new image (no database ID, hence 'isNew' or a large timestamp)
                if (imageToDelete && (imageToDelete.isNew || typeof imageToDelete.id === 'number' && imageToDelete.id > 1000000000000)) { 
                    setWorkingImages(prev => prev.filter(img => img.id !== id));
                    showAlert({ type: 'success', title: 'Deleted!', message: 'The new image has been removed from the list.' });
                    return;
                }
        
                // For existing images, call the backend API
                const response = await deleteData(`${deletePath}/${id}`);
        
                if (response.status === 200 || response.data.message) {
                    setWorkingImages(prev => prev.filter(img => img.id !== id));
                    showAlert({ type: 'success', title: 'Deleted!', message: 'The image has been permanently deleted from the server.' });
                }
            } catch (error) {
                console.error('Delete error:', error);
                const errorMessage = error.response?.data?.message || 'Failed to delete image. Please check the network connection.';
                showAlert({ type: 'error', title: 'Deletion Failed', message: errorMessage });
            }
        });
    };

    const handleEdit = (id, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const imageToEdit = workingImages.find(img => img.id === id);
        if (imageToEdit) {
            setNewImage({
                file: null,
                displayOrder: imageToEdit.displayOrder.toString(),
                description: imageToEdit.description || '',
                file_name: imageToEdit.file_name || ''
            });
            setEditId(id);
            setShowUploadForm(true);
        }
    };

    const handleViewImage = (file, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (file) {
            setSelectedImage(file);
            setIsModalOpen(true);
        }
    };

    const columns = [
        // ... columns remain the same
        { key: 'index', label: 'NO', sortable: false, align: 'center' },
        { 
            key: 'view', 
            label: 'View', 
            sortable: false,
            align: 'center',
            width: '100px'
        },
        { 
            key: 'displayOrder', 
            label: 'Order', 
            sortable: true,
            align: 'center'
        },
        { 
            key: 'description', 
            label: 'Description', 
            sortable: true 
        },
        { 
            key: 'actions', 
            label: 'Actions', 
            sortable: false,
            align: 'center',
            width: '120px'
        }
    ];

    const tableData = React.useMemo(() => {
        return workingImages
            .filter(img => !img.isDeleted)
            .map((image, index) => ({
                ...image,
                index: index + 1,
                view: image.file_name || 'New Image',
                actions: image.id
            }));
    }, [workingImages]);

    const customRenderers = {
        // ... customRenderers remain the same
        view: (row) => (
            <div className="flex justify-center">
                <button
                    type="button"
                    onClick={(e) => handleViewImage(row.file, e)}
                    className="text-blue-500 hover:text-blue-700"
                    title="View Image"
                    disabled={!row.file}
                >
                    <FaImage size={18} />
                </button>
            </div>
        ),
        actions: (row) => (
            <div className="flex gap-2 justify-center">
                <button
                    className="bg-blue-500 text-white p-1 rounded hover:bg-blue-600"
                    onClick={(e) => handleEdit(row.id, e)}
                >
                    <FaEdit />
                </button>
                <button
                    className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                    onClick={(e) => handleDelete(row.id, e)}
                >
                    <FaTrashAlt />
                </button>
            </div>
        ),
    };

    return (
        // ... JSX remains the same
        <div className="p-6 w-full">
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl max-h-screen overflow-auto">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-semibold">Image Preview</h3>
                            <button 
                                onClick={() => setIsModalOpen(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <FaTimes size={20} />
                            </button>
                        </div>
                        <div className="p-4">
                            {selectedImage && (
                                <img 
                                    src={selectedImage instanceof File ? URL.createObjectURL(selectedImage) : selectedImage}
                                    alt="Preview" 
                                    className="max-w-full max-h-[80vh] object-contain"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold"> Banner Images</h1>
                {!showUploadForm && (
                    <Button
                        variant="danger"
                        onClick={() => setShowUploadForm(true)}
                        className="flex items-center px-4 py-2"
                    >
                        <FaPlus className="mr-2" />
                        Add Image
                    </Button>
                )}
            </div>

            <div className="mb-8">
                <GenericTable
                    columns={columns}
                    data={tableData}
                    customRenderers={customRenderers}
                    rowKey="id"
                    stripedRows={true}
                    hoverEffect={true}
                    headerClassName="bg-red-700 text-white"
                    emptyMessage="No images added yet"
                />
            </div>

            {showUploadForm && (
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4">
                        {editId !== null ? 'Update Image' : 'Upload New Image'}
                    </h2>

                    <div className="grid grid-cols-1 gap-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Input
                                type="file"
                                label="Image File"
                                name="file"
                                onChange={handleFileChange}
                                accept="image/*"
                                className="w-full"
                                required={!editId}
                                helperText={editId ? `Current file: ${newImage.file_name || 'No file selected'}` : undefined}
                            />

                            <Input
                                type="number"
                                label="Display Order"
                                name="displayOrder"
                                value={newImage.displayOrder || ''}
                                onChange={handleInputChange}
                                placeholder="Enter display order"
                                className="w-full"
                                min="0"
                            />
                        </div>

                        <div>
                            <Input
                                type="text"
                                label="Description"
                                name="description"
                                value={newImage.description}
                                onChange={handleInputChange}
                                placeholder="Enter image description"
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end space-x-4 mt-6">
                        <Button
                            variant="secondary"
                            onClick={resetForm}
                            className="flex items-center px-4 py-2"
                        >
                            <FaTimes className="mr-2" />
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleSaveImage}
                            className="flex items-center px-4 py-2"
                            disabled={!newImage.file && !editId}
                        >
                            <FaUpload className="mr-2" />
                            {editId !== null ? 'Update Image' : 'Upload Image'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductGroupImg;